import { FACULTIES, type ClubProposalDto } from "@clubap/shared";
import { useCallback, useEffect, useState } from "react";
import { approveProposal, fetchPendingProposals, rejectProposal } from "../social/api.js";
import { LogoPreview } from "./FoundClubPanel.js";

interface Props {
  onClose: () => void;
}

/** El nombre de una facultad, o "Transversal" cuando no se afilia a ninguna. */
function facultad(id: string | null): string {
  if (id === null) return "Transversal";
  return FACULTIES.find((entry) => entry.id === id)?.name ?? id;
}

function fecha(at: number): string {
  return new Date(at).toLocaleDateString("es", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * La bandeja del administrador: qué clubes están esperando permiso para existir.
 *
 * Sólo se monta para un admin, y eso es comodidad, no defensa: los tres endpoints que
 * toca vuelven a preguntar por la capacidad. Un cliente modificado no lee este archivo.
 *
 * Se enseñan de la más VIEJA a la más nueva porque una bandeja se atiende por orden de
 * llegada: con las nuevas arriba, la primera solicitud del semestre se hunde y nadie la
 * responde nunca.
 */
export function ClubProposalsPanel({ onClose }: Props) {
  const [proposals, setProposals] = useState<ClubProposalDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const recargar = useCallback(async () => {
    try {
      const pendientes = await fetchPendingProposals();
      /* El orden se impone acá y no se hereda del servidor: es una decisión de esta
         pantalla, y si mañana el endpoint cambia de criterio la bandeja no se entera. */
      setProposals([...pendientes].sort((a, b) => a.createdAt - b.createdAt));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudieron cargar las solicitudes.");
      setProposals([]);
    }
  }, []);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const decidir = async (proposal: ClubProposalDto, aprobar: boolean) => {
    setBusy(proposal.id);
    setError(null);

    try {
      if (aprobar) await approveProposal(proposal.id);
      else await rejectProposal(proposal.id);

      /* Se vuelve a pedir la lista en vez de quitar la fila de la pantalla: entre que
         se abrió la bandeja y se pulsó el botón puede haber llegado otra, y puede
         haberla resuelto otro admin desde su sesión. */
      await recargar();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo resolver la solicitud.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="overlay">
      <div className="panel sheet club-sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">Clubes por aprobar</h2>
          {proposals && <span className="sheet__coins">{proposals.length}</span>}
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        {proposals === null && <p className="sheet__hint">Cargando las solicitudes…</p>}

        {proposals !== null && proposals.length === 0 && !error && (
          <div className="sheet__empty">
            <p>No hay ninguna solicitud esperando.</p>
            <p className="sheet__hint">Cuando alguien funde un club, aparece acá.</p>
          </div>
        )}

        {proposals !== null && proposals.length > 0 && (
          <ul className="sheet__list">
            {proposals.map((proposal) => (
              <li className="club-prop" key={proposal.id}>
                <div className="club-prop__cara">
                  <span
                    className="badge club-marca"
                    style={{ color: proposal.color, borderColor: proposal.color }}
                    title={`Color ${proposal.color}`}
                  >
                    <LogoPreview logo={proposal.logo} code={proposal.code} size={40} />
                  </span>

                  <span className="club-prop__texto">
                    <span className="club-siglas">{proposal.code}</span>
                    <span className="sheet__name">{proposal.name}</span>
                    {proposal.blurb && <span className="social__blurb">{proposal.blurb}</span>}
                  </span>
                </div>

                <p className="club-prop__datos">
                  <span>{facultad(proposal.facultyId)}</span>
                  <span>·</span>
                  <span>La manda {proposal.from.username}</span>
                  <span>·</span>
                  <span>{fecha(proposal.createdAt)}</span>
                  {!proposal.logo && (
                    <>
                      <span>·</span>
                      <span>sin logo</span>
                    </>
                  )}
                </p>

                <div className="club-acciones">
                  <button
                    className="btn btn--small"
                    type="button"
                    disabled={busy !== null}
                    title={`Dejar nacer a ${proposal.name}`}
                    onClick={() => void decidir(proposal, true)}
                  >
                    {busy === proposal.id ? "…" : "Aprobar"}
                  </button>

                  <button
                    className="social__quiet"
                    type="button"
                    disabled={busy !== null}
                    title={`Rechazar la solicitud de ${proposal.from.username}`}
                    onClick={() => void decidir(proposal, false)}
                  >
                    Rechazar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <p className="sheet__hint">
          Al aprobar, el club nace con su sede, su caja y quien lo pidió presidiéndolo. El
          logo se guarda saneado: mira que la insignia se vea bien antes de decidir.
        </p>
      </div>
    </div>
  );
}
